import React, { useEffect, useState, useCallback } from "react";
import TestimonialManager from "../../services/dashboard/TestimonialManager";
import TestimonialModal from "./TestimonialModal";
import AvatarImage from "../common/AvatarImage";
import config from "../../config";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { 
  FaFileCsv, 
  FaFileExcel, 
  FaCheck, 
  FaTimes, 
  FaClock, 
  FaSpinner, 
  FaStar,
  FaRegStar,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEyeSlash,
  FaSearch,
  FaChevronDown,
  FaBookOpen
} from "react-icons/fa";

const TestimonialManagerComponent = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [filteredTestimonials, setFilteredTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionMessage, setActionMessage] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({ 
    show: false, 
    testimonialId: null, 
    action: null, 
    testimonialName: "" 
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    featured: "all",
    rating: "all",
    sortBy: "newest"
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    featured: 0
  });
  useEffect(() => {
    fetchTestimonials();
  }, []);

  // Avatar URL generation with fallback logic
  const getAvatarUrl = (testimonial) => {
    // If testimonial has an uploaded avatar image, use it
    if (testimonial.avatar && testimonial.avatar.startsWith('http')) {
      return testimonial.avatar;
    }
    
    // If testimonial has an uploaded file name (starts with avatar_ and has extension), construct the URL
    if (testimonial.avatar && testimonial.avatar.startsWith('avatar_') && testimonial.avatar.includes('.')) {
      // This is an uploaded file name, construct the full URL using config
      return `${config.url}/uploads/avatars/${testimonial.avatar}`;
    }
    
    // For short avatar values (initials) or no avatar, fallback to generated avatar using DiceBear
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(testimonial.name)}&backgroundColor=f97316,fb923c,fbbf24`;
  };

  const fetchTestimonials = async () => {
    try {
      console.log("Fetching testimonials...");
      const data = await TestimonialManager.getAllTestimonials();
      console.log("Testimonials data received:", data);
      setTestimonials(data || []);
    } catch (error) {
      console.error("Failed to fetch testimonials:", error);
      console.error("Error details:", error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = useCallback(() => {
    const total = testimonials.length;
    const approved = testimonials.filter(t => t.status === "APPROVED").length;
    const pending = testimonials.filter(t => t.status === "PENDING").length;
    const featured = testimonials.filter(t => t.isFeatured).length;
    
    setStats({ total, approved, pending, featured });
  }, [testimonials]);

  const applyFilters = useCallback(() => {
    let filtered = [...testimonials];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(t => 
        t.name?.toLowerCase().includes(searchLower) ||
        t.text?.toLowerCase().includes(searchLower) ||
        t.location?.toLowerCase().includes(searchLower) ||
        t.persona?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (filters.status !== "all") {
      filtered = filtered.filter(t => t.status === filters.status);
    }

    // Featured filter
    if (filters.featured !== "all") {
      const isFeatured = filters.featured === "featured";
      filtered = filtered.filter(t => t.isFeatured === isFeatured);
    }

    // Rating filter
    if (filters.rating !== "all") {
      const minRating = parseInt(filters.rating);
      filtered = filtered.filter(t => t.rating >= minRating);
    }

    // Sort
    switch (filters.sortBy) {
      case "newest":
        filtered.sort((a, b) => new Date(b.creationDate) - new Date(a.creationDate));
        break;
      case "oldest":
        filtered.sort((a, b) => new Date(a.creationDate) - new Date(b.creationDate));
        break;
      case "rating":
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case "name":
        filtered.sort((a, b) => a.name?.localeCompare(b.name));
        break;
      default:
        break;
    }

    setFilteredTestimonials(filtered);
  }, [testimonials, filters]);

  useEffect(() => {
    applyFilters();
    calculateStats();
  }, [applyFilters, calculateStats]);
  const handleStatusChange = async (testimonialId, status) => {
    setIsProcessing(true);
    try {
      await TestimonialManager.updateTestimonialStatus(testimonialId, status);
      setActionMessage(`✅ Testimonial ${status.toLowerCase()} successfully!`);
      fetchTestimonials();
      setConfirmDialog({ show: false, testimonialId: null, action: null, testimonialName: "" });
      
      setTimeout(() => setActionMessage(""), 5000);
    } catch (err) {
      setActionMessage("❌ Failed to update status.");
      setTimeout(() => setActionMessage(""), 5000);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleToggleFeatured = async (testimonialId) => {
    setIsProcessing(true);
    try {
      await TestimonialManager.toggleFeatured(testimonialId);
      setActionMessage("✅ Featured status updated successfully!");
      fetchTestimonials();
      
      setTimeout(() => setActionMessage(""), 5000);
    } catch (err) {
      setActionMessage("❌ Failed to update featured status.");
      setTimeout(() => setActionMessage(""), 5000);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async (testimonialId) => {
    setIsProcessing(true);
    try {
      await TestimonialManager.deleteTestimonial(testimonialId);
      setActionMessage("✅ Testimonial deleted successfully!");
      fetchTestimonials();
      setConfirmDialog({ show: false, testimonialId: null, action: null, testimonialName: "" });
      
      setTimeout(() => setActionMessage(""), 5000);
    } catch (err) {
      setActionMessage("❌ Failed to delete testimonial.");
      setTimeout(() => setActionMessage(""), 5000);
    } finally {
      setIsProcessing(false);
    }
  };

  const showConfirmDialog = (testimonialId, action, testimonialName) => {
    setConfirmDialog({ show: true, testimonialId, action, testimonialName });
  };

  const closeConfirmDialog = () => {
    if (!isProcessing) {
      setConfirmDialog({ show: false, testimonialId: null, action: null, testimonialName: "" });
    }
  };
  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: {
        icon: FaClock,
        className: "bg-amber-500/10 text-amber-600 border-amber-500/20 animate-pulse",
        label: "Pending"
      },
      APPROVED: {
        icon: FaCheck,
        className: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20 shadow-emerald-500/20 shadow-sm",
        label: "Approved"
      },
      REJECTED: {
        icon: FaTimes,
        className: "bg-rose-500/10 text-rose-600 border-rose-500/20",
        label: "Rejected"
      },
      ARCHIVED: {
        icon: FaEyeSlash,
        className: "bg-gray-500/10 text-gray-600 border-gray-500/20",
        label: "Archived"
      }
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.className}`}>
        <Icon className="w-3 h-3 mr-1.5" />
        {config.label}
      </span>
    );
  };

  const getFeaturedBadge = (isFeatured) => {
    if (!isFeatured) return null;
    
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-orange-500/20 to-amber-500/20 text-orange-600 border border-orange-500/30 shadow-orange-500/20 shadow-sm">
        <FaStar className="w-3 h-3 mr-1.5" />
        Featured
      </span>
    );
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          star <= rating ? (
            <FaStar key={star} className="w-3.5 h-3.5 text-amber-400" />
          ) : (
            <FaRegStar key={star} className="w-3.5 h-3.5 text-gray-300 dark:text-gray-400" />
          )
        ))}
        <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          {rating}.0
        </span>
      </div>
    );
  };
  const renderActionButtons = (testimonial) => {
    return (
      <div className="flex items-center gap-1">
        {/* Featured Toggle */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleToggleFeatured(testimonial.testimonialId);
          }}
          className={`p-2 rounded-lg transition-all duration-200 hover:scale-105 ${
            testimonial.isFeatured
              ? "bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-500/30"
              : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
          }`}
          title={testimonial.isFeatured ? "Remove from featured" : "Add to featured"}
        >
          <FaStar className="w-3.5 h-3.5" />
        </button>

        {/* Status Actions */}
        {testimonial.status === "PENDING" && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                showConfirmDialog(testimonial.testimonialId, "APPROVED", testimonial.name);
              }}
              className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-500/30 hover:scale-105 transition-all duration-200"
              title="Approve testimonial"
            >
              <FaCheck className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                showConfirmDialog(testimonial.testimonialId, "REJECTED", testimonial.name);
              }}
              className="p-2 rounded-lg bg-rose-100 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400 hover:bg-rose-200 dark:hover:bg-rose-500/30 hover:scale-105 transition-all duration-200"
              title="Reject testimonial"
            >
              <FaTimes className="w-3.5 h-3.5" />
            </button>
          </>
        )}

        {/* Edit Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setEditingTestimonial(testimonial);
          }}
          className="p-2 rounded-lg bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-500/30 hover:scale-105 transition-all duration-200"
          title="Edit testimonial"
        >
          <FaEdit className="w-3.5 h-3.5" />
        </button>

        {/* Delete Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            showConfirmDialog(testimonial.testimonialId, "DELETE", testimonial.name);
          }}
          className="p-2 rounded-lg bg-rose-100 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400 hover:bg-rose-200 dark:hover:bg-rose-500/30 hover:scale-105 transition-all duration-200"
          title="Delete testimonial"
        >
          <FaTrash className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  };
  const mapTestimonialsForExport = () => {
    return testimonials.map((testimonial) => ({
      ID: testimonial.testimonialId,
      Name: testimonial.name || "",
      Location: testimonial.location || "",
      Persona: testimonial.persona || "",
      Text: testimonial.text || "",
      Rating: testimonial.rating || "",
      Status: testimonial.status || "",
      Featured: testimonial.isFeatured ? "Yes" : "No",
      Verified: testimonial.isVerified ? "Yes" : "No",
      "Display Order": testimonial.displayOrder || "",
      "Created At": testimonial.creationDate
        ? formatDateTimeFromArray(testimonial.creationDate)
        : "",
    }));
  };

  const handleDownloadCSV = () => {
    const exportData = mapTestimonialsForExport();
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Testimonials");

    const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csvOutput], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "testimonials.csv");
  };

  const handleDownloadExcel = () => {
    const exportData = mapTestimonialsForExport();
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Testimonials");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "testimonials.xlsx");
  };

  const formatDateTimeFromArray = (arr) => {
    if (!Array.isArray(arr) || arr.length < 6) return "";
    
    const dateTime = new Date(
      arr[0],        // year
      arr[1] - 1,    // month (0-indexed)
      arr[2],        // day
      arr[3] || 0,   // hour
      arr[4] || 0,   // minute
      arr[5] || 0    // second
    ).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
    
    return dateTime;
  };
  const handleModalSave = async (formData) => {
    setModalLoading(true);
    try {
      if (editingTestimonial) {
        await TestimonialManager.updateTestimonial(editingTestimonial.testimonialId, formData);
        setActionMessage("✅ Testimonial updated successfully!");
      } else {
        await TestimonialManager.createTestimonial(formData);
        setActionMessage("✅ Testimonial created successfully!");
      }
      
      fetchTestimonials();
      setShowCreateModal(false);
      setEditingTestimonial(null);
      
      setTimeout(() => setActionMessage(""), 5000);
    } catch (err) {
      setActionMessage("❌ Failed to save testimonial.");
      setTimeout(() => setActionMessage(""), 5000);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setEditingTestimonial(null);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      status: "all",
      featured: "all",
      rating: "all",
      sortBy: "newest"
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0B0F19] text-gray-900 dark:text-white">
      {/* Background Effects */}
      <div className="fixed inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-[#0B0F19] dark:via-[#111827] dark:to-[#0B0F19] opacity-50"></div>
      <div 
        className="fixed inset-0 opacity-40" 
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.02'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      ></div>
      
      <div className="relative z-10">
        <div className="max-w-7xl mx-auto px-6 py-8">
          
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">Testimonials</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">Manage customer testimonials and reviews</p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-orange-500/25"
              >
                <FaPlus className="w-4 h-4" />
                Add testimonial
              </button>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDownloadCSV}
                  className="p-2 bg-white dark:bg-[#1F2937] hover:bg-gray-50 dark:hover:bg-[#374151] text-gray-600 dark:text-gray-300 rounded-lg transition-all duration-200 hover:scale-105 border border-gray-200 dark:border-white/10"
                  title="Export CSV"
                >
                  <FaFileCsv className="w-4 h-4" />
                </button>
                <button
                  onClick={handleDownloadExcel}
                  className="p-2 bg-white dark:bg-[#1F2937] hover:bg-gray-50 dark:hover:bg-[#374151] text-gray-600 dark:text-gray-300 rounded-lg transition-all duration-200 hover:scale-105 border border-gray-200 dark:border-white/10"
                  title="Export Excel"
                >
                  <FaFileExcel className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          {/* Inline Stats Row - Stripe Style */}
          <div className="flex items-center gap-6 mb-6">
            <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-[#111827] rounded-lg border border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20 transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/10">
              <FaBookOpen className="w-4 h-4 text-blue-500 dark:text-blue-400" />
              <span className="text-sm text-gray-600 dark:text-gray-300">Total</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{stats.total}</span>
            </div>
            
            <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-[#111827] rounded-lg border border-gray-200 dark:border-white/10 hover:border-emerald-300 dark:hover:border-emerald-500/30 transition-all duration-200 hover:shadow-lg hover:shadow-emerald-500/10">
              <FaCheck className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
              <span className="text-sm text-gray-600 dark:text-gray-300">Approved</span>
              <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">{stats.approved}</span>
            </div>
            
            <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-[#111827] rounded-lg border border-gray-200 dark:border-white/10 hover:border-amber-300 dark:hover:border-amber-500/30 transition-all duration-200 hover:shadow-lg hover:shadow-amber-500/10">
              <FaClock className="w-4 h-4 text-amber-500 dark:text-amber-400" />
              <span className="text-sm text-gray-600 dark:text-gray-300">Pending</span>
              <span className="text-sm font-semibold text-amber-600 dark:text-amber-400">{stats.pending}</span>
            </div>
            
            <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-[#111827] rounded-lg border border-gray-200 dark:border-white/10 hover:border-orange-300 dark:hover:border-orange-500/30 transition-all duration-200 hover:shadow-lg hover:shadow-orange-500/10">
              <FaStar className="w-4 h-4 text-orange-500 dark:text-orange-400" />
              <span className="text-sm text-gray-600 dark:text-gray-300">Featured</span>
              <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">{stats.featured}</span>
            </div>
          </div>

          {/* Action Message */}
          {actionMessage && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-lg">
              <p className="text-blue-700 dark:text-blue-400 text-sm">{actionMessage}</p>
            </div>
          )}

          {/* Sticky Filter Bar - Glassmorphism */}
          <div className="sticky top-0 z-20 mb-6 p-4 bg-white/80 dark:bg-[#111827]/80 backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-xl shadow-2xl">
            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search testimonials..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-[#1F2937] border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-200"
                />
              </div>

              {/* Status Filter */}
              <div className="relative">
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  className="appearance-none bg-white dark:bg-[#1F2937] border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2.5 pr-8 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-200"
                >
                  <option value="all">All Status</option>
                  <option value="PENDING">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="ARCHIVED">Archived</option>
                </select>
                <FaChevronDown className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 pointer-events-none" />
              </div>
              {/* Featured Filter */}
              <div className="relative">
                <select
                  value={filters.featured}
                  onChange={(e) => handleFilterChange("featured", e.target.value)}
                  className="appearance-none bg-white dark:bg-[#1F2937] border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2.5 pr-8 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-200"
                >
                  <option value="all">All Featured</option>
                  <option value="featured">Featured</option>
                  <option value="not-featured">Not Featured</option>
                </select>
                <FaChevronDown className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 pointer-events-none" />
              </div>

              {/* Rating Filter */}
              <div className="relative">
                <select
                  value={filters.rating}
                  onChange={(e) => handleFilterChange("rating", e.target.value)}
                  className="appearance-none bg-white dark:bg-[#1F2937] border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2.5 pr-8 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-200"
                >
                  <option value="all">All Ratings</option>
                  <option value="5">★ 5 Stars</option>
                  <option value="4">★ 4+ Stars</option>
                  <option value="3">★ 3+ Stars</option>
                </select>
                <FaChevronDown className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 pointer-events-none" />
              </div>

              {/* Sort */}
              <div className="relative">
                <select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange("sortBy", e.target.value)}
                  className="appearance-none bg-white dark:bg-[#1F2937] border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2.5 pr-8 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-200"
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="rating">Highest Rated</option>
                  <option value="name">Name A-Z</option>
                </select>
                <FaChevronDown className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 pointer-events-none" />
              </div>

              {/* Clear Filters */}
              {(filters.search || filters.status !== "all" || filters.featured !== "all" || filters.rating !== "all" || filters.sortBy !== "newest") && (
                <button
                  onClick={clearFilters}
                  className="px-3 py-2.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white text-sm transition-colors duration-200"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          {/* Linear-Style Data Grid */}
          <div className="bg-white dark:bg-[#111827] rounded-xl border border-gray-200 dark:border-white/10 overflow-hidden shadow-2xl">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                  <span className="text-gray-500 dark:text-gray-400">Loading testimonials...</span>
                </div>
              </div>
            ) : filteredTestimonials.length === 0 ? (
              <div className="text-center py-16">
                <FaBookOpen className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No testimonials found</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {testimonials.length === 0 
                    ? "Get started by adding your first testimonial."
                    : "Try adjusting your filters to see more results."
                  }
                </p>
                {testimonials.length === 0 && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-all duration-200 hover:scale-105"
                  >
                    <FaPlus className="w-4 h-4" />
                    Add First Testimonial
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-[#1F2937]/50 border-b border-gray-200 dark:border-white/10">
                    <tr>
                      <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Customer</th>
                      <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Testimonial</th>
                      <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Rating</th>
                      <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Featured</th>
                      <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                      <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-white/5">
                    {filteredTestimonials.map((testimonial, index) => (
                      <tr
                        key={testimonial.testimonialId}
                        className={`group cursor-pointer transition-all duration-200 hover:bg-gray-50 dark:hover:bg-[#1F2937]/50 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-orange-500/5 ${
                          expandedRow === testimonial.testimonialId ? 'bg-gray-50 dark:bg-[#1F2937]/30' : ''
                        }`}
                        onClick={() => setExpandedRow(expandedRow === testimonial.testimonialId ? null : testimonial.testimonialId)}
                        onMouseEnter={() => setHoveredRow(testimonial.testimonialId)}
                        onMouseLeave={() => setHoveredRow(null)}
                      >
                        {/* Customer Info */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <AvatarImage
                              src={getAvatarUrl(testimonial)}
                              name={testimonial.name}
                              size="md"
                              className="shadow-lg group-hover:scale-110 transition-transform duration-200"
                            />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{testimonial.name}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                {testimonial.persona && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30">
                                    {testimonial.persona}
                                  </span>
                                )}
                                {testimonial.location && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">{testimonial.location}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Testimonial Text */}
                        <td className="px-6 py-4">
                          <div className="max-w-md">
                            <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 leading-relaxed">
                              {testimonial.text}
                            </p>
                            {expandedRow === testimonial.testimonialId && (
                              <div className="mt-3 p-3 bg-gray-100 dark:bg-[#1F2937] rounded-lg border border-gray-200 dark:border-white/10">
                                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                                  {testimonial.text}
                                </p>
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Rating */}
                        <td className="px-6 py-4">
                          {renderStars(testimonial.rating)}
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4">
                          {getStatusBadge(testimonial.status)}
                        </td>

                        {/* Featured */}
                        <td className="px-6 py-4">
                          {getFeaturedBadge(testimonial.isFeatured)}
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4">
                          <div className={`transition-opacity duration-200 ${hoveredRow === testimonial.testimonialId ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                            {renderActionButtons(testimonial)}
                          </div>
                        </td>

                        {/* Date */}
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {testimonial.creationDate ? formatDateTimeFromArray(testimonial.creationDate) : "-"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Premium Confirmation Dialog */}
      {confirmDialog.show && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#111827] border border-gray-200 dark:border-white/10 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800">
                {confirmDialog.action === "APPROVED" ? (
                  <FaCheck className="w-6 h-6 text-emerald-500 dark:text-emerald-400" />
                ) : confirmDialog.action === "REJECTED" ? (
                  <FaTimes className="w-6 h-6 text-rose-500 dark:text-rose-400" />
                ) : (
                  <FaTrash className="w-6 h-6 text-rose-500 dark:text-rose-400" />
                )}
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
                {confirmDialog.action === "APPROVED" ? "Approve Testimonial" : 
                 confirmDialog.action === "REJECTED" ? "Reject Testimonial" : 
                 "Delete Testimonial"}
              </h3>
              
              <p className="text-gray-600 dark:text-gray-400 text-center mb-6 text-sm leading-relaxed">
                Are you sure you want to {confirmDialog.action?.toLowerCase()} the testimonial from{" "}
                <span className="font-medium text-gray-900 dark:text-white">"{confirmDialog.testimonialName}"</span>?
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={closeConfirmDialog}
                  disabled={isProcessing}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isProcessing 
                      ? "text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 cursor-not-allowed" 
                      : "text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#1F2937] hover:bg-gray-200 dark:hover:bg-[#374151] border border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (confirmDialog.action === "DELETE") {
                      handleDelete(confirmDialog.testimonialId);
                    } else {
                      handleStatusChange(confirmDialog.testimonialId, confirmDialog.action);
                    }
                  }}
                  disabled={isProcessing}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-all duration-200 flex items-center justify-center ${
                    confirmDialog.action === "APPROVED"
                      ? isProcessing 
                        ? "bg-emerald-600/50 cursor-not-allowed" 
                        : "bg-emerald-600 hover:bg-emerald-700 shadow-lg hover:shadow-emerald-500/25"
                      : isProcessing 
                        ? "bg-rose-600/50 cursor-not-allowed" 
                        : "bg-rose-600 hover:bg-rose-700 shadow-lg hover:shadow-rose-500/25"
                  }`}
                >
                  {isProcessing ? (
                    <>
                      <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    confirmDialog.action === "APPROVED" ? "Approve" : 
                    confirmDialog.action === "REJECTED" ? "Reject" : "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Testimonial Modal */}
      <TestimonialModal
        isOpen={showCreateModal || editingTestimonial !== null}
        onClose={handleCloseModal}
        onSave={handleModalSave}
        testimonial={editingTestimonial}
        isLoading={modalLoading}
      />

      {/* Custom Styles */}
      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        /* Custom scrollbar */
        .overflow-x-auto::-webkit-scrollbar {
          height: 6px;
        }
        .overflow-x-auto::-webkit-scrollbar-track {
          background: rgba(229, 231, 235, 0.5);
        }
        .dark .overflow-x-auto::-webkit-scrollbar-track {
          background: rgba(31, 41, 55, 0.5);
        }
        .overflow-x-auto::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.5);
          border-radius: 3px;
        }
        .dark .overflow-x-auto::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.3);
        }
        .overflow-x-auto:hover::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.7);
        }
        .dark .overflow-x-auto:hover::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.5);
        }
        
        /* Focus ring */
        input:focus, select:focus {
          box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
        }
        .dark input:focus, .dark select:focus {
          box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
        }
      `}</style>
    </div>
  );
};

export default TestimonialManagerComponent;